<template lang="pug">
.content
  ed-title-nav(title='Dashboard', subtitle='Summary of statuses and recent activity' :fixed='false')
    span(slot='action')
      ed-button(v-if='widgets.length || id' @click='addWidget' class='hidden') Add Widgets
  .dashboard-wrapper
    .container-fluid.no-padding
      .main-body
        chart-tooltip
        new-dashboard(v-if='!widgets.length && dashboardResolved || !newDashboard'
                      @create='create'
                      :container-id='id'
                      :new-dashboard='id ? false : newDashboard'
                      :action='newAction'
                      ref='newWidget'
                      :endpoint='api.$.dashboard.getData'
        )
        grid-layout(:layout='widgets', :col-num='12'
                    :is-draggable='true'
                    :is-resizable='false'
                    :row-height='ROW_HEIGHT'
                    :use-css-transforms='false'
                    :margin="[30, 20]"
                    ref='gridLayout'
        )
          grid-item.widget(v-for='(widget, i) in widgets'
                           :is-resizable='isResizable(widget)'
                           :key='widget.id'
                           :i='widget.i'
                           :min-w='4', :max-w='12'
                           :min-h='widget.h'
                           :max-h='widget.mh'

                           :x='widget.x', :y='widget.y'
                           :w='widget.w', :h='widget.h'

                           drag-allow-from='.widget-header'
                           drag-ignore-from='.no-drag'
                           @moved='(idx, newX, newY) => moved(widget, i, newX, newY)'
                           :style='{ minHeight: `${widget.h * ROW_HEIGHT}px` }'
                           ref='gridItems'
          )
            ed-widget(:id='widget.id'
                      :value='widget'
                      :title='getTitle(widget)'
                      :settings='widget.settings'
                      :toolbar='!widget.component'
                      :position=`{
                                  isCollapsed: widget.isCollapsed,
                                  x: widget.x,
                                  y: widget.y,
                                  w: widget.w,
                                  h: widget.h,
                                  height: widget.h * ROW_HEIGHT,
                                  offset
                                }`
                      draggable='true'
                      @removed='removed(widget, i)'
                      @cloned='cloned(widget, $event)'
                      @saved='saved(widget)'
                      ref='widgets'
            )
              template.content(slot='content' slot-scope='props')
                component(v-if='widget.component' :is='widget.component' :id='`W-${widget.id}`' ref='charts' :widget='widget')
</template>

<script>
/* eslint no-multi-spaces: 0 */
/* eslint key-spacing    : 0 */

import VueGridLayout from 'vue-grid-layout'
import NewDashboard from './new'
import { ROW_HEIGHT, WIDTHS, MIN_ROWS, MAX_ROWS, getWidgetSize } from './widgets/SharedComponents/constants'
import throttle from 'lodash/throttle'
import { EventBus } from '@/event-bus'
import ChartTooltip from '@/core/charts/components/chart-tooltip'
import components from './widgets'
import featureFlags from '@/core/feature-flags/feature-flags'
import { routeOldLinks } from '@/components/LegacyContainer/routeOldLinks'

export default {
  name: 'Dashboard',

  components: {
    GridLayout : VueGridLayout.GridLayout,
    GridItem   : VueGridLayout.GridItem,
    ChartTooltip,
    NewDashboard
  },

  data() {
    return {
      ROW_HEIGHT,
      MIN_ROWS,
      MAX_ROWS,
      newDashboard: true,
      newAction: 'Create Dashboard',
      dashboardResolved: null,
      id: null,
      widgets: [],
      offset: 0
    }
  },

  activated() {
    this.widgets = []
    this.dashboardResolved = false
    this.getDashboard(true)
  },

  created() {
    this.getDashboard()
    EventBus.$on('updateNavigatedAwayFrom', r => {
      this.taintedWidget = r
    })
    EventBus.$on('widgetCollapseStateChanged', r => {
      this.updateWidgetCollapseState(r.data.wId, r.data.pos)
    })
    EventBus.$on('updateWidgetHeight', r => {
      this.updateWidgetHeight(r.data.wId, r.data.height, r.data.alt, r.data.state || null)
    })
    EventBus.$on('updateWidgetHeightSimple', r => {
      this.updateWidgetHeightSimple(r.data.wId, r.data.height, r.data.explicitS || null)
    })
  },

  watch: {
    '$route' (to, from) {
      if (to.params.page === 'Dashboard' && from.params.page === 'Reports') {
        if (this.taintedWidget) {
          this.getDashboardWidget(this.id, this.taintedWidget.data)
        }

        this.taintedWidget = null
      }
    }
  },

  mounted() {
    this.bindScroll()
    this.bindResize()
  },

  methods: {
    /**
     * Get dashboard
     *
     */
    getDashboard(activated) {
      if (featureFlags.legacyViewDisabled) {
        routeOldLinks.call(this, this.$route, '', false)
      }

      this.api.get(this.api.$.dashboard.container).then(r => {
        if (r.data && r.data[0] && r.data[0].id) {
          this.id = r.data[0].id
          this.getDashboardWidgets(this.id, activated)

          this.newAction = 'Add Widgets'
        } else {
          this.dashboardResolved = true
        }
      })
    },

    /**
     * Get dashboard widgets based on id
     *
     */
    getDashboardWidgets(id, activated) {
      const source = id ? [`${this.api.$.dashboard.widgets}/${id}`] : [`${this.api.$.dashboard.getData}`]
      const fetch = id ? this.api.get : this.api.post

      fetch(...source).then(r => {
        if (r.data) {
          this.widgets = this.mix(r.data)
          this.dashboardResolved = true
          activated &&
          EventBus.$emit('DASHBOARD_READY')
        }
      })
    },

    updateWidgetHeightSimple(wId, heightInPX, explicitS) {
      let desiredH = 0
      if (explicitS) desiredH = explicitS
      else desiredH = Math.ceil(heightInPX / (ROW_HEIGHT + 10)) - 1
      this.updateWidgetHeight(wId, desiredH, -1, null)
    },

    setDesiredWidgetHeight(widget, state) {
      let desiredH = 0
      if (state === 'OK') desiredH = getWidgetSize(widget.renderComponent).MIN_ROWS
      else if (state === 'NA') desiredH = getWidgetSize(widget.renderComponent).NA_ROWS
      else if (state === 'EX') desiredH = getWidgetSize(widget.renderComponent).EX_MAX_ROWS
      widget.storedHeight = desiredH
      return desiredH - 1
    },

    updateWidgetHeight(wId, offset, alt, state) {
      let widget = this.getWigetById(wId)
      let widgetSizes = getWidgetSize(widget.renderComponent)
      let maxHeight = widgetSizes.MAX_ROWS
      if (state) {
        offset = this.setDesiredWidgetHeight(widget, state)
        let upDownMod = widgetSizes.NA_ROWS > widgetSizes.MAX_ROWS ? 1 : -1
        if (state === 'NA') {
          alt = upDownMod * (widgetSizes.MAX_ROWS) + 1
        } else alt = upDownMod * (widgetSizes.NA_ROWS) + 1
      }
      if (widget.h !== (1 + offset)) {
        offset = offset > maxHeight ? maxHeight : offset
        if (this.widgets.length !== 0) {
          let affectedWidgets =  this.widgets.filter(w => w.x === widget.x && w.y > widget.y)
          affectedWidgets.forEach(wgt => {
            wgt.y += offset + alt
          })
        }
        this.$nextTick(() => {
          offset = widget.isCollapsed ? 1 : (1 + offset)
          widget.h = offset
          if (this.$refs.gridLayout) {
            this.$refs.gridLayout.eventBus.$emit(
                'resizeEvent',
                'resizeend',
                widget.i,
                widget.x,
                widget.y,
                offset,
                widget.w
              )
          }
        })
      }
      this.dashboardResolved = true
    },

    getWigetById(wId) {
      let widgetIndex = this.widgets.findIndex(w => w.id === wId)
      return this.widgets[widgetIndex]
    },

    updateWidgetCollapseState(wId, newState) {
      let widget = this.getWigetById(wId)
      let originalHeight = widget.isCollapsed ? widget.storedHeight || getWidgetSize(widget.renderComponent).MIN_ROWS : widget.h
      widget.isCollapsed = newState[0].dockInfo.isCollapsed
      let offset =  !widget.isCollapsed ? (originalHeight - 1) : (originalHeight - 1) * -1
      this.updateWidgetHeight(wId, offset, 0)
    },

    getDashboardWidget(cId, wId) {
      if (cId) {
        const source = [`${this.api.$.dashboard.widget}/${cId}/${wId}`]
        const fetch = this.api.get
        fetch(...source).then(r => {
          if (r.data) {
            this.reloadWidget(wId, r.data[0])
            this.dashboardResolved = true
          }
        })
      }
    },
    /**
     * Set dashboard settings
     *
     */
    setDashboard(id, payload) {
      this.api.put(`${this.api.$.dashboard.widgets}`, payload)
    },

    /**
     * Creates a new dashboard
     *
     */
    create(widgets) {
      this.widgets = widgets
    },

    /**
     * Creates a new widget
     *
     */
    addWidget() {
      if (this.newDashboard) {
        this.newDashboard = false
        this.id && this.$refs.newWidget && this.$refs.newWidget.showModules()
      } else {
        this.$refs.newWidget.showModules()
      }
    },

    /**
     * Gets the widget title
     *
     */
    getTitle(widget) {
      return widget.settings && widget.settings.title || widget.title || widget.id
    },

    /**
     * Clones widget
     *
     */
    cloned(widget, payload) {
      const widgetClone = deepClone(widget)
      const layoutRows  = Math.max(...this.widgets.map(w => w.y))
      widgetClone.title = (widget.label || widget.id) + ' [copy]'
      widgetClone.settings.title = widgetClone.title
      widgetClone.label = widgetClone.title
      widgetClone.id    = payload.id || widget.id + '1'
      widgetClone.i     = this.widgets.length + 1 + ''
      widgetClone.y     = layoutRows + 1
      widgetClone._dateFilter = [new Date(widget.dateRangeFilter.startDate), new Date(widget.dateRangeFilter.endDate)]
      this.$nextTick(() => {
        this.widgets.push(widgetClone)
        if (payload.id) {
          this.getDashboardWidget(this.id, payload.id)
        }
      })
    },

    /**
     * Removes widget
     *
     */
    removed(widget, i) {
      const adjacentWidget = this.widgets[i - 1 >= 0 ? i - 1 : i + 1]

      // remove widget
      this.widgets.splice(this.widgets.indexOf(widget), 1)

      if (this.widgets.length) {
        this.redraw(adjacentWidget)
      }
    },

    /**
     * Changes widget position in layout
     *
     */
    moved(widget, i, newX, newY) {
      const availablePositions = [0, 6]
      const isValidPosition = availablePositions.indexOf(newX) >= 0
      this.$el.querySelector('.vue-grid-placeholder').setAttribute('data-valid', isValidPosition)
      let position = [
        {
          widgetId: widget.id,
          dockInfo: {
            dockRow: newY,
            dockOrder: isValidPosition ? newX : this.closest(newX, availablePositions),
            isCollapsed : widget.isCollapsed
          }
        }
      ]
      this.$nextTick(() => {
        widget.x = position[0].dockInfo.dockOrder
      })
      this.setDashboard(this.id, position)
    },

    reloadChart(widget) {
      const widgetChart = this.$refs.charts.filter(r => r.id === `W-${widget.id}`)[0]
      widgetChart && widgetChart.$emit('scrollIntoView')
    },

    /**
     * Redraws chart/grid inside widget
     *
     */
    redraw(widget, i) {
      const widgetComp     = widget.component && this.$refs.comps.filter(r => r.$attrs.id === `${widget.id}-comp`)[0]
      const widgetCompGrid = widgetComp &&
                             ~widgetComp.$children[0].$vnode.tag.indexOf('-grid') &&
                             widgetComp.$children[0]

      if (widgetCompGrid) {
        widgetCompGrid.$emit('redraw')
      }
      if (this.$refs.widgets[i]) this.$refs.widgets[i].startWO = widget.w
      this.$nextTick(() => {
        this.snapToWidth(widget, i)
      })
      // reset drag/resize placeholder
      this.$el.querySelector('.vue-grid-placeholder').setAttribute('data-valid', true)
    },

    resizeFromHandle(widget, i, newH, newW) {
      const availableWidths = getAvailableWidths(widget)
      this.$el.querySelector('.vue-grid-placeholder').setAttribute('data-valid', availableWidths.indexOf(newW) >= 0)
      this.$refs.widgets[i].startW = widget.w
    },

    snapToWidth(widget, i) {
      const availableWidths = getAvailableWidths(widget)
      const snappedWidth    = this.closest(widget.w, availableWidths)
      const $widget         = this.$refs.widgets[i]
      widget.w = snappedWidth
      if ($widget && ($widget.startW !== snappedWidth || $widget.startWO !== $widget.startW)) {
        this.$nextTick(() => {
          const widgetChart = this.$refs.charts.filter(r => r.id === `W-${widget.id}`)[0]
          widgetChart && widgetChart.$emit('redraw')
          $widget.$emit('resize')
        })
      }
    },

    closest(value, list) {
      let v = list[0]
      let d = Math.abs(value - v)
      for (let i = 0; i < list.length; i++) {
        let c = Math.abs(value - list[i])
        if (d > c) {
          d = c
          v = list[i]
        }
      }
      return v
    },

    reloadWidget(wId, newData) {
      let widget = this.getWigetById(wId)
      widget.variables = newData.variables
      widget._dateFilter = [new Date(newData.dateRangeFilter.startDate), new Date(newData.dateRangeFilter.endDate)]
      widget.title = newData.label
      widget.isCollapsed = newData.dockInfo.isCollapsed
      widget.reqDraw = true
      widget.h =  !widget.isCollapsed ? getWidgetSize(widget.renderComponent).MIN_ROWS : 1
      widget.mh = getWidgetSize(widget.renderComponent).MAX_ROWS
      widget.reportFilters = newData.reportFilters
      widget.settings = {
        title  : newData.title,
        details: {
          label  : '',
          action : ''
        }
      }
      widget.actions = newData.buttons.map(button => {
        button.ref = button.action
        button.status = button.visible
        return button
      })
      this.saved(widget)
      this.taintedWidget = null
    },
    /**
     * Saves widget settings
     *
     */
    saved(widget) {
      this.reloadChart(widget)
    },

    /**
     * Prepares the layout
     *
     */
    mix(widgets) {
      return widgets.map(widget => {
        // TODO: extract to function
        widget.x = widget.dockInfo.dockOrder
        widget.y = widget.dockInfo.dockRow
        widget.isCollapsed = widget.dockInfo.isCollapsed
        widget.reqDraw = true
        widget.w = WIDTHS[widget.variables.filter(k => k.name === 'Width')[0].value]
        widget.h =  !widget.isCollapsed ? getWidgetSize(widget.renderComponent).MIN_ROWS : 1
        widget.mh = getWidgetSize(widget.renderComponent).MAX_ROWS
        widget.i = `${widget.id}`

        widget._dateFilter = [new Date(widget.dateRangeFilter.startDate), new Date(widget.dateRangeFilter.endDate)]

        widget.title = widget.label
        widget.settings = {
          title  : widget.title,
          details: {
            label  : '',
            action : ''
          }
        }
        widget.actions = widget.buttons.map(button => {
          button.ref = button.action
          button.status = button.visible
          return button
        })
        widget.component = components[widget.renderComponent]
        return widget
      })
    },

    isResizable(widget) {
      return widget.variableDefinitions &&
             widget.variableDefinitions[0].items.length > 1
    },

    /**
     * Passes scroll offset to widgets
     * to trigger lazy load
     *
     */
    bindScroll() {
      document.querySelector('.main-panel').addEventListener('scroll', throttle(this.handleScroll, 400))
    },
    handleScroll(e) {
      this.offset = e.target.scrollTop
    },

    /**
     * Updates innerHeight
     *
     */
    bindResize() {
      window.addEventListener('resize', throttle(this.handleResize, 1000))
    },
    handleResize(e) {
      this.$root.innerHeight = window.innerHeight
    }

  },

  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll)
    window.removeEventListener('resize', this.handleResize)

    EventBus.$off('updateWidgetHeight')
    EventBus.$off('updateWidgetHeightSimple')
  }
}

//
function getAvailableWidths(widget) {
  return widget.variableDefinitions
    ? widget.variableDefinitions[0].items.map(k => WIDTHS[k.key])
    : Object.keys(WIDTHS).map(k => WIDTHS[k])
}

/**
 * Returns a deeply cloned object without reference.
 * Copied from Vuex.
 * @type {Object}
 */
function deepClone (obj) {
  if (Array.isArray(obj)) {
    return obj.map(deepClone)
  } else if (obj && typeof obj === 'object') {
    var cloned = {}
    var keys = Object.keys(obj)
    for (var i = 0, l = keys.length; i < l; i++) {
      var key = keys[i]
      cloned[key] = deepClone(obj[key])
    }
    return cloned
  } else {
    return obj
  }
}

</script>

<style scoped lang='scss'>
@import "~@/assets/sass/_variables.scss";

@at-root .theme {
  .main-panel > .content {
    // margin-top: 5rem;
  }
}

.dashboard-wrapper {
  padding: 1em;
  .alternateTop{
    z-index: 9;
  }
.container-fluid.no-padding{
  padding-left: 0;
  padding-right: 0;
}
  .main-body {
    margin-left: -.5em;
    margin-right: -.5em;
  }
  .inline.padded {
    padding-right: 15px;
  }
}

</style>



// WEBPACK FOOTER //
// src/components/_modules/Dashboard/index.vue